import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import { billingApi } from '../api/billing'
import type { PlanChangeResult, Subscription } from '../api/billing'
import { creditsApi } from '../api/credits'
import type { CreditsResponse, Plan } from '../api/types'
import { useAuth } from '../auth/useAuth'
import { Alert } from '../components/Alert'
import { Modal } from '../components/Modal'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { formatCredits, formatDateTime, formatInt, formatMoney } from '../lib/format'

/** `section` splits the one billing surface the contract exposes into the two
 *  tabs the Account hub asks for: `plan` is the plan / allowance / top-up
 *  summary plus the buy-credits flow, `ledger` is the credit ledger and its
 *  pager. `all` (the default, used by the standalone route) renders both in the
 *  order they have always appeared. */
export type BillingSection = 'all' | 'plan' | 'ledger'

/** `embedded` drops the page chrome (breadcrumb, title, `.wrap` padding) so the
 *  Account hub can host this screen inside a tab panel. Both fetches run in
 *  every mode, so the API calls never change. */
export function BillingPage({
  embedded = false,
  section = 'all',
}: {
  embedded?: boolean
  section?: BillingSection
}) {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const credits = useAsync<CreditsResponse>(() => creditsApi.get(page, 25), [page])
  const plans = useAsync<Plan[]>(() => creditsApi.plans(), [])
  const [buyOpen, setBuyOpen] = useState(false)
  const [buying, setBuying] = useState<string | null>(null)
  const [buyError, setBuyError] = useState<string | null>(null)

  // Plan changes. `pendingChange` holds the plan awaiting confirmation, because
  // an upgrade charges a card and a downgrade removes capability — neither
  // should happen on a single stray click.
  const [changeOpen, setChangeOpen] = useState(false)
  const [pendingChange, setPendingChange] = useState<Plan | null>(null)
  const [changing, setChanging] = useState(false)
  const [changeError, setChangeError] = useState<string | null>(null)
  const [changeDone, setChangeDone] = useState<PlanChangeResult | null>(null)
  const [cancelOpen, setCancelOpen] = useState(false)

  const subscription = useAsync<Subscription>(() => billingApi.subscription(), [])

  // Coming back from a hosted checkout. The gateway redirects here whether the
  // purchase succeeded, was abandoned or failed, so the outcome is read from the
  // API rather than inferred from the return — and the reads are re-run, because
  // the credits and the subscription were both changed by the webhook handling
  // this payment.
  //
  // Seeded once from the URL rather than set inside the effect: the banner has
  // to outlive the parameter being stripped, and a reload should not re-announce
  // a purchase that happened earlier.
  const returnedFromCheckout = params.get('billing') === 'done'
  const [checkoutDone] = useState(() => params.get('billing') === 'done')
  const creditReload = credits.reload
  const planReload = plans.reload
  const subscriptionReload = subscription.reload

  useEffect(() => {
    if (!returnedFromCheckout) return

    creditReload()
    planReload()
    subscriptionReload()

    // Stripped so a refresh does not re-announce a completed purchase. The
    // banner keeps showing, because its state was seeded above.
    const next = new URLSearchParams(params)
    next.delete('billing')
    setParams(next, { replace: true })
  }, [returnedFromCheckout, params, setParams, creditReload, planReload, subscriptionReload])

  const balance = credits.data?.balance ?? user?.credits ?? null
  const currentPlan = plans.data?.find((plan) => plan.isCurrent) ?? plans.data?.[0] ?? null
  const packs = currentPlan?.packs ?? []

  const purchase = async (packId: string) => {
    setBuying(packId)
    setBuyError(null)
    try {
      const { checkoutUrl } = await creditsApi.purchase(packId)
      // Checkout is hosted by the payment gateway, so hand the browser over.
      window.location.assign(checkoutUrl)
    } catch (cause: unknown) {
      setBuyError(cause instanceof ApiError ? cause.message : 'Could not start checkout.')
      setBuying(null)
    }
  }

  /**
   * Applies the confirmed plan change.
   *
   * An account that has never paid goes to hosted checkout instead: there is no
   * subscription to reprice, so `POST /billing/plan` would have nothing to act
   * on and the customer has no card on file to charge.
   */
  const applyPlanChange = async (plan: Plan) => {
    setChanging(true)
    setChangeError(null)
    try {
      if (!subscription.data?.isPaid && plan.priceUsd > 0) {
        const { checkoutUrl } = await billingApi.checkout(plan.id)
        window.location.assign(checkoutUrl)
        return
      }

      const result = await billingApi.changePlan(plan.id)
      setChangeDone(result)
      setPendingChange(null)

      // Both reads move: the plan drives the allowance, and an upgrade releases
      // credits the ledger has just recorded.
      subscription.reload()
      plans.reload()
      credits.reload()
    } catch (cause: unknown) {
      setChangeError(cause instanceof ApiError ? cause.message : 'Could not change your plan.')
    } finally {
      setChanging(false)
    }
  }

  const cancelSubscription = async () => {
    setChanging(true)
    setChangeError(null)
    try {
      await billingApi.cancel()
      setCancelOpen(false)
      subscription.reload()
    } catch (cause: unknown) {
      setChangeError(cause instanceof ApiError ? cause.message : 'Could not cancel your subscription.')
    } finally {
      setChanging(false)
    }
  }

  const grantedTotal = balance ? balance.included + balance.purchased : 0
  const usedPct = grantedTotal > 0 ? Math.min(100, (balance!.used / grantedTotal) * 100) : 0

  // Top-ups are a per-plan capability, and the plan that does not allow them is
  // the trial — exactly the plan a new account is on. The API refuses with
  // TOP_UP_UNAVAILABLE, so offering the button unconditionally hands the user a
  // click that can only fail.
  const canTopUp = packs.length > 0

  const showPlan = section !== 'ledger'
  const showLedger = section !== 'plan'

  const body = (
    <>
      {embedded ? null : (
        <div className="phead">
          <div>
            <div className="crumb">
              <Link to="/app">Home</Link> / Billing
            </div>
            <h2>Billing &amp; credits</h2>
          </div>
          <span className="spacer" />
          {canTopUp ? (
            <button className="btn btn-p" type="button" onClick={() => setBuyOpen(true)}>
              Buy credits
            </button>
          ) : null}
        </div>
      )}

      {checkoutDone ? (
        <Alert tone="info" title="Payment received" style={{ marginBottom: 18 }}>
          Your purchase is confirmed. Credits and any plan change can take a moment to appear while
          the payment settles.
        </Alert>
      ) : null}

      {showPlan ? (
        <>
          <div className="grid3" style={{ gap: 16, marginBottom: 18 }}>
            <div className="card card-p">
              <div className="fs12 faint" style={{ textTransform: 'uppercase', letterSpacing: '.09em', marginBottom: 8 }}>
                Current plan
              </div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 24, marginBottom: 4 }}>
                {currentPlan?.name ?? user?.planName ?? '—'}
              </div>
              {currentPlan ? (
                <>
                  <p className="fs13 muted" style={{ margin: '0 0 14px' }}>
                    Up to {currentPlan.maxCompanies} companies · {currentPlan.maxUsers} users ·{' '}
                    {formatCredits(currentPlan.monthlyCredits)} credits/month
                  </p>
                  <div className="fs13">
                    <b className="mono" style={{ fontSize: 17 }}>
                      {formatMoney(currentPlan.priceUsd)}
                    </b>{' '}
                    <span className="muted">/ month, billed monthly</span>
                  </div>

                  {/* A scheduled downgrade is the one piece of state a customer
                      cannot infer from the plan name, so it is always shown. */}
                  {subscription.data?.pendingPlanName ? (
                    <p className="fs12" style={{ margin: '10px 0 0', color: 'var(--amber-ink, #b06f05)' }}>
                      Moving to <b>{subscription.data.pendingPlanName}</b>
                      {subscription.data.currentPeriodEnd
                        ? ` on ${new Date(subscription.data.currentPeriodEnd).toLocaleDateString()}`
                        : ' at the end of this period'}
                      .
                    </p>
                  ) : null}

                  {subscription.data?.cancelAtPeriodEnd ? (
                    <p className="fs12" style={{ margin: '10px 0 0', color: 'var(--amber-ink, #b06f05)' }}>
                      Cancels
                      {subscription.data.currentPeriodEnd
                        ? ` on ${new Date(subscription.data.currentPeriodEnd).toLocaleDateString()}`
                        : ' at the end of this period'}
                      . You keep full access until then.
                    </p>
                  ) : null}

                  {subscription.data?.status === 'PastDue' ? (
                    <p className="fs12" style={{ margin: '10px 0 0', color: 'var(--red, #e11d48)' }}>
                      The last payment failed. Update your card to keep this plan.
                    </p>
                  ) : null}

                  <div className="row" style={{ gap: 8, marginTop: 14 }}>
                    <button
                      className="btn btn-o btn-sm"
                      type="button"
                      onClick={() => {
                        setChangeDone(null)
                        setChangeError(null)
                        setChangeOpen(true)
                      }}
                    >
                      Change plan
                    </button>
                    {subscription.data?.isPaid && !subscription.data.cancelAtPeriodEnd ? (
                      <button
                        className="btn btn-o btn-sm"
                        type="button"
                        onClick={() => setCancelOpen(true)}
                      >
                        Cancel
                      </button>
                    ) : null}
                  </div>
                </>
              ) : (
                <p className="fs13 muted" style={{ margin: 0 }}>
                  Plan details are not available right now.
                </p>
              )}
            </div>

            <div className="card card-p">
              <div className="fs12 faint" style={{ textTransform: 'uppercase', letterSpacing: '.09em', marginBottom: 8 }}>
                Credits this cycle
              </div>
              <div className="row" style={{ alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                <span className="mono" style={{ fontSize: 26, fontWeight: 600 }}>
                  {formatCredits(balance?.used ?? 0)}
                </span>
                <span className="muted fs13">of {formatCredits(grantedTotal)} used</span>
              </div>
              <div className="prog" style={{ marginBottom: 10 }}>
                <i style={{ width: `${usedPct}%` }} />
              </div>
              <p className="fs12 faint" style={{ margin: 0 }}>
                {formatCredits(balance?.available ?? 0)} left
                {balance?.reserved ? ` · ${formatCredits(balance.reserved)} reserved by running jobs` : ''}.
                Unused monthly credits do not roll over; purchased top-ups never expire.
              </p>
            </div>

            <div className="card card-p">
              <div className="fs12 faint" style={{ textTransform: 'uppercase', letterSpacing: '.09em', marginBottom: 8 }}>
                Top-up balance
              </div>
              <div className="row" style={{ alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                <span className="mono" style={{ fontSize: 26, fontWeight: 600 }}>
                  {formatCredits(balance?.purchased ?? 0)}
                </span>
                <span className="muted fs13">credits</span>
              </div>
              <p className="fs12 faint" style={{ margin: '0 0 12px' }}>
                {canTopUp
                  ? 'Drawn down only after the monthly allowance runs out. Purchased credits never expire.'
                  : `Top-ups are not available on ${currentPlan?.name ?? 'this plan'}. Move to a paid plan to buy extra credits.`}
              </p>
              {canTopUp ? (
                <button className="btn btn-o btn-sm" type="button" onClick={() => setBuyOpen(true)}>
                  Top up
                </button>
              ) : (
                <button
                  className="btn btn-o btn-sm"
                  type="button"
                  onClick={() => {
                    setChangeDone(null)
                    setChangeError(null)
                    setChangeOpen(true)
                  }}
                >
                  View plans
                </button>
              )}
            </div>
          </div>

          {buyError ? (
            <Alert tone="err" title="Checkout did not start" style={{ marginBottom: 18 }}>
              {buyError}
            </Alert>
          ) : null}
        </>
      ) : null}

      {showLedger ? (
        <div className="card">
          <div className="card-h">
            <h3>Where the credits went</h3>
            <span className="spacer" />
            <span className="fs12 faint">Newest first</span>
          </div>

          {credits.error ? (
            <ErrorState error={credits.error} onRetry={credits.reload} />
          ) : credits.loading && !credits.data ? (
            <LoadingState label="Loading your ledger…" />
          ) : (credits.data?.ledger.items.length ?? 0) === 0 ? (
            <EmptyState
              icon="≡"
              title="Nothing charged yet"
              body="Every job that touches QuickBooks lands here with its rate and credit cost."
            />
          ) : (
            <div className="tscroll">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Operation</th>
                    <th>Rate</th>
                    <th className="n">Volume</th>
                    <th className="n">Credits</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {credits.data!.ledger.items.map((entry) => (
                    <tr key={entry.id}>
                      <td className="fs13 mono">{formatDateTime(entry.occurredAt)}</td>
                      <td className="fs13">
                        {entry.operation}
                        {entry.description ? (
                          <div className="fs12 faint">{entry.description}</div>
                        ) : null}
                      </td>
                      <td className="fs13 mono">{entry.rate} / unit</td>
                      <td className="n mono fs13">{formatCredits(entry.quantity)}</td>
                      <td className="n mono fs13">
                        <b>{formatCredits(entry.credits)}</b>
                      </td>
                      <td>
                        {entry.jobId ? (
                          <Link className="btn btn-g btn-sm" to={`/app/jobs/${entry.jobId}`}>
                            Job
                          </Link>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card-f">
            <span className="fs13 muted">
              {formatCredits(credits.data?.ledger.total ?? 0)} ledger entries
            </span>
            <span className="spacer" />
            <button
              className="btn btn-g btn-sm"
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              ← Previous
            </button>
            <button
              className="btn btn-g btn-sm"
              type="button"
              disabled={(credits.data?.ledger.items.length ?? 0) < 25}
              onClick={() => setPage((current) => current + 1)}
            >
              Next →
            </button>
          </div>
        </div>
      ) : null}

      {showPlan ? (
        <Modal open={buyOpen} title="Buy credits" onClose={() => setBuyOpen(false)}>
          {plans.error ? (
            <ErrorState error={plans.error} onRetry={plans.reload} />
          ) : packs.length === 0 ? (
            <p className="fs13 muted" style={{ margin: 0 }}>
              No credit packs are on sale for your plan right now.
            </p>
          ) : (
            <div className="bb-packpick">
              <p className="bb-planpick-intro">
                Credits are added to your account as soon as the payment completes. They are drawn
                down only after your monthly allowance is used up, and they never expire.
              </p>

              {buyError ? (
                <Alert tone="err" title="Checkout did not start" style={{ marginBottom: 14 }}>
                  {buyError}
                </Alert>
              ) : null}

              {packs.map((pack) => (
                <div className="bb-planrow bb-packrow" key={pack.id}>
                  <div className="bb-planrow-main">
                    <div className="bb-planrow-head">
                      <b>{formatInt(pack.credits)} credits</b>
                    </div>
                    {/* PRD 7.2 prices every pack at a flat $10 per 1,000 credits,
                        so a per-credit rate would be the same number on every
                        row and compare nothing.

                        How far a pack goes is deliberately not stated: cost per
                        unit differs by operation — an imported line is 1 credit,
                        an exported transaction 0.1 — so any single figure would
                        be wrong for most readers. The rate belongs on the ledger
                        and the operation screens, where the action is known. */}
                    <div className="bb-planrow-spec">
                      <span>{formatMoney(pack.priceUsd / (pack.credits / 1000))} per 1,000 credits</span>
                      <span>Never expires</span>
                    </div>
                  </div>

                  <div className="bb-planrow-buy">
                    <span className="bb-planrow-price">{formatMoney(pack.priceUsd)}</span>
                    <button
                      className="bb-btn bb-btn-green"
                      type="button"
                      disabled={buying !== null}
                      onClick={() => void purchase(pack.id)}
                    >
                      {buying === pack.id ? 'Opening…' : 'Buy'}
                    </button>
                  </div>
                </div>
              ))}

              <p className="fs12 faint" style={{ margin: '14px 0 0' }}>
                You will be taken to a secure checkout to pay, then returned here.
              </p>
            </div>
          )}
        </Modal>
      ) : null}

      {/* Plan picker. Every active plan is listed rather than only the ones
          "above" the current tier, because a downgrade is a legitimate move and
          hiding it just sends the customer to support. */}
      {showPlan ? (
        <Modal
          open={changeOpen}
          title={changeDone ? 'Plan updated' : pendingChange ? 'Confirm plan change' : 'Change plan'}
          onClose={() => {
            setChangeOpen(false)
            setPendingChange(null)
            setChangeDone(null)
          }}
        >
          {changeError ? (
            <Alert tone="err" title="Could not change your plan" style={{ marginBottom: 14 }}>
              {changeError}
            </Alert>
          ) : null}

          {changeDone ? (
            <div>
              <p className="fs13" style={{ margin: '0 0 16px' }}>
                {changeDone.message}
              </p>
              <button
                className="btn btn-p btn-sm"
                type="button"
                onClick={() => {
                  setChangeOpen(false)
                  setChangeDone(null)
                }}
              >
                Done
              </button>
            </div>
          ) : pendingChange ? (
            <PlanChangeConfirmation
              plan={pendingChange}
              current={currentPlan}
              busy={changing}
              onBack={() => setPendingChange(null)}
              onConfirm={() => void applyPlanChange(pendingChange)}
            />
          ) : (
            <div className="bb-planpick">
              <p className="bb-planpick-intro">
                Changing plan takes effect immediately when you upgrade, and at the end of the
                current period when you move down.
              </p>

              {(plans.data ?? [])
                .filter((plan) => !plan.isCurrent)
                .map((plan) => {
                  const isUpgrade = currentPlan ? plan.priceUsd > currentPlan.priceUsd : true

                  return (
                    <div
                      className={plan.isQuoteOnly ? 'bb-planrow bb-planrow-off' : 'bb-planrow'}
                      key={plan.id}
                    >
                      <div className="bb-planrow-main">
                        <div className="bb-planrow-head">
                          <b>{plan.name}</b>
                          {plan.isQuoteOnly ? (
                            <span className="bb-planrow-tag">Quoted</span>
                          ) : isUpgrade ? (
                            <span className="bb-planrow-tag bb-planrow-tag-up">Upgrade</span>
                          ) : (
                            <span className="bb-planrow-tag">Downgrade</span>
                          )}
                        </div>

                        {/* Quote-only plans are priced at 0 with unlimited limits,
                            which would read as "0 credits, 0 companies" — so the
                            limits are described in words instead of numbers. */}
                        <div className="bb-planrow-spec">
                          {plan.isQuoteOnly ? (
                            <>
                              <span>Unlimited credits</span>
                              <span>Unlimited companies</span>
                              <span>Unlimited users</span>
                            </>
                          ) : (
                            <>
                              <span>{formatInt(plan.monthlyCredits)} credits / month</span>
                              <span>
                                {plan.maxCompanies} {plan.maxCompanies === 1 ? 'company' : 'companies'}
                              </span>
                              <span>{plan.maxUsers} users</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="bb-planrow-buy">
                        <span className="bb-planrow-price">
                          {plan.isQuoteOnly ? 'Contact us' : formatMoney(plan.priceUsd)}
                          {plan.isQuoteOnly ? null : <em>/mo</em>}
                        </span>

                        {plan.isQuoteOnly ? (
                          // Deliberately not a button: the API refuses this move,
                          // so offering it would guarantee a failed click.
                          <span className="bb-planrow-note">Talk to sales</span>
                        ) : (
                          <button
                            className="bb-btn bb-btn-outline"
                            type="button"
                            onClick={() => setPendingChange(plan)}
                          >
                            {isUpgrade ? 'Upgrade' : 'Switch'}
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
            </div>
          )}
        </Modal>
      ) : null}

      <Modal
        open={cancelOpen}
        title="Cancel your subscription?"
        onClose={() => setCancelOpen(false)}
        footer={
          <>
            <button className="btn btn-o" type="button" onClick={() => setCancelOpen(false)}>
              Keep it
            </button>
            <span className="spacer" />
            <button
              className="btn btn-p"
              type="button"
              disabled={changing}
              onClick={() => void cancelSubscription()}
            >
              {changing ? 'Cancelling…' : 'Cancel subscription'}
            </button>
          </>
        }
      >
        <p className="fs13" style={{ margin: 0 }}>
          Your plan stays active until{' '}
          {subscription.data?.currentPeriodEnd
            ? new Date(subscription.data.currentPeriodEnd).toLocaleDateString()
            : 'the end of the current period'}
          , and you keep full access until then. No refund is issued for the remainder, and you can
          resubscribe at any time.
        </p>
      </Modal>
    </>
  )

  return embedded ? body : <div className="wrap">{body}</div>
}

/**
 * The confirmation step, which states what will actually happen.
 *
 * The two outcomes differ enough to be worth spelling out: an upgrade charges a
 * prorated amount today and raises the allowance at once, while a downgrade
 * changes nothing until the period ends. A customer who expects the wrong one
 * has a support ticket either way.
 */
function PlanChangeConfirmation({
  plan,
  current,
  busy,
  onBack,
  onConfirm,
}: {
  plan: Plan
  current: Plan | null
  busy: boolean
  onBack: () => void
  onConfirm: () => void
}) {
  const isUpgrade = current !== null && plan.priceUsd > current.priceUsd
  const creditDelta = current ? plan.monthlyCredits - current.monthlyCredits : plan.monthlyCredits

  return (
    <div>
      <p className="fs13" style={{ margin: '0 0 12px' }}>
        Moving from <b>{current?.name ?? 'your current plan'}</b> to <b>{plan.name}</b> at{' '}
        <span className="mono">{formatMoney(plan.priceUsd)}</span> per month.
      </p>

      {isUpgrade ? (
        <ul className="fs13" style={{ margin: '0 0 16px', paddingLeft: 18 }}>
          <li>Takes effect immediately.</li>
          <li>You are charged a prorated amount for the rest of this billing period.</li>
          {creditDelta > 0 ? <li>{formatCredits(creditDelta)} more credits are available right away.</li> : null}
        </ul>
      ) : (
        <ul className="fs13" style={{ margin: '0 0 16px', paddingLeft: 18 }}>
          <li>Takes effect at the end of the current billing period.</li>
          <li>You keep your current plan and its full allowance until then.</li>
          <li>No refund is issued for the remainder of this period.</li>
        </ul>
      )}

      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-o btn-sm" type="button" onClick={onBack} disabled={busy}>
          Back
        </button>
        <span className="spacer" />
        <button className="btn btn-p btn-sm" type="button" onClick={onConfirm} disabled={busy}>
          {busy ? 'Working…' : isUpgrade ? 'Upgrade now' : 'Schedule change'}
        </button>
      </div>
    </div>
  )
}
